import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  Card,
  CardMedia,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RecyclingOutlined as ServiceIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Image as ImageIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { serviciosAPI, uploadAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';

export default function Servicios() {
  const queryClient = useQueryClient();
  const organizacion = useAuthStore((state) => state.user?.organizacion);
  const fileInputRef = useRef(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingServicio, setEditingServicio] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, servicioId: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Cargar servicios
  const { data: servicios, isLoading } = useQuery({
    queryKey: ['servicios'],
    queryFn: async () => {
      const response = await serviciosAPI.listar();
      return response.data.data;
    },
  });

  // Mutación para crear servicio
  const crearMutation = useMutation({
    mutationFn: serviciosAPI.crear,
    onSuccess: () => {
      queryClient.invalidateQueries(['servicios']);
      handleCloseDialog();
      showSnackbar('Servicio creado exitosamente', 'success');
    },
    onError: (error) => {
      showSnackbar(error.response?.data?.error?.message || 'Error al crear servicio', 'error');
    },
  });

  // Mutación para actualizar servicio
  const actualizarMutation = useMutation({
    mutationFn: ({ id, data }) => serviciosAPI.actualizar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['servicios']);
      handleCloseDialog();
      showSnackbar('Servicio actualizado exitosamente', 'success');
    },
    onError: (error) => {
      showSnackbar(error.response?.data?.error?.message || 'Error al actualizar servicio', 'error');
    },
  });

  // Mutación para eliminar servicio
  const eliminarMutation = useMutation({
    mutationFn: serviciosAPI.eliminar,
    onSuccess: () => {
      queryClient.invalidateQueries(['servicios']);
      setDeleteConfirm({ open: false, servicioId: null });
      showSnackbar('Servicio eliminado exitosamente', 'success');
    },
    onError: (error) => {
      showSnackbar(error.response?.data?.error?.message || 'Error al eliminar servicio', 'error');
    },
  });

  function getEmptyForm() {
    return {
      IdServicio: '',
      NombreServicio: '',
      Imagen: '',
    };
  }

  const handleOpenDialog = (servicio = null) => {
    if (servicio) {
      setEditingServicio(servicio);
      setFormData({
        IdServicio: servicio.IdServicio,
        NombreServicio: servicio.NombreServicio,
        Imagen: servicio.Imagen,
      });
      setImagePreview(null);
    } else {
      setEditingServicio(null);
      setFormData(getEmptyForm());
      setImagePreview(null);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingServicio(null);
    setFormData(getEmptyForm());
    setFormErrors({});
    setImagePreview(null);
    setSelectedFile(null);
    setUploading(false);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.IdServicio) errors.IdServicio = 'ID Servicio es requerido';
    if (!formData.NombreServicio) errors.NombreServicio = 'Nombre del servicio es requerido';
    if (!formData.Imagen) errors.Imagen = 'Imagen es requerida';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      showSnackbar('Por favor complete todos los campos obligatorios', 'warning');
      return;
    }

    const data = {
      ...formData,
      IdServicio: parseInt(formData.IdServicio),
    };

    if (editingServicio) {
      actualizarMutation.mutate({ id: editingServicio.Id, data });
    } else {
      crearMutation.mutate(data);
    }
  };

  const handleDeleteClick = async (servicio) => {
    try {
      const response = await serviciosAPI.verificarEliminacion(servicio.Id);
      if (response.data.data.canDelete) {
        setDeleteConfirm({ open: true, servicioId: servicio.Id });
      } else {
        showSnackbar(response.data.data.message, 'warning');
      }
    } catch {
      showSnackbar('Error al verificar eliminación', 'error');
    }
  };

  const handleDeleteConfirm = () => {
    eliminarMutation.mutate(deleteConfirm.servicioId);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    if (field === 'Imagen') {
      setImagePreview(value);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar que sea una imagen
      if (!file.type.startsWith('image/')) {
        showSnackbar('Por favor seleccione un archivo de imagen válido', 'error');
        return;
      }

      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('La imagen no debe superar los 5MB', 'error');
        return;
      }

      setSelectedFile(file);

      // Crear preview local
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImage = async () => {
    if (!selectedFile) {
      showSnackbar('Por favor seleccione una imagen primero', 'warning');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.subirImagen(selectedFile, organizacion);
      const filename = response.data.archivo.nombre;

      // Actualizar el campo Imagen con el nombre del archivo
      handleChange('Imagen', filename);
      setImagePreview(`/uploads/${organizacion}/${filename}`);
      showSnackbar('Imagen subida exitosamente', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      showSnackbar('Error al subir la imagen', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ServiceIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4">Servicios</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Nuevo Servicio
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nombre del Servicio</TableCell>
              <TableCell>Imagen</TableCell>
              <TableCell align="center">Recolecciones</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : servicios && servicios.length > 0 ? (
              servicios.map((servicio) => (
                <TableRow key={servicio.Id}>
                  <TableCell>{servicio.IdServicio}</TableCell>
                  <TableCell>{servicio.NombreServicio}</TableCell>
                  <TableCell>
                    {servicio.Imagen && (
                      <Box
                        component="img"
                        src={`/uploads/${organizacion}/${servicio.Imagen}`}
                        alt={servicio.NombreServicio}
                        sx={{
                          width: 50,
                          height: 50,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid #ccc',
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">{servicio.Qty_items || 0}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenDialog(servicio)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        servicio.Qty_items > 0
                          ? 'No se puede eliminar (tiene recolecciones)'
                          : 'Eliminar'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(servicio)}
                          disabled={servicio.Qty_items > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No hay servicios registrados
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Formulario */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingServicio ? 'Editar Servicio' : 'Nuevo Servicio'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ID Servicio *"
                value={formData.IdServicio}
                onChange={(e) => handleChange('IdServicio', e.target.value)}
                error={!!formErrors.IdServicio}
                helperText={formErrors.IdServicio}
                disabled={!!editingServicio}
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre del Servicio *"
                value={formData.NombreServicio}
                onChange={(e) => handleChange('NombreServicio', e.target.value)}
                error={!!formErrors.NombreServicio}
                helperText={formErrors.NombreServicio}
              />
            </Grid>

            {/* Imagen actual (solo en modo edición) */}
            {editingServicio && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Imagen actual
                </Typography>
                <Card variant="outlined">
                  {formData.Imagen ? (
                    <CardMedia
                      component="img"
                      height="180"
                      image={
                        imagePreview?.startsWith('data:')
                          ? imagePreview
                          : `/uploads/${organizacion}/${formData.Imagen}`
                      }
                      alt={formData.NombreServicio}
                      sx={{ objectFit: 'contain', bgcolor: 'grey.50' }}
                      onError={(e) => {
                        e.target.src = '';
                        e.target.alt = 'No se pudo cargar la imagen';
                        e.target.parentElement.style.display = 'flex';
                        e.target.parentElement.style.alignItems = 'center';
                        e.target.parentElement.style.justifyContent = 'center';
                        e.target.parentElement.style.height = '80px';
                        e.target.style.display = 'none';
                        const txt = document.createElement('span');
                        txt.textContent = 'No se pudo cargar la imagen';
                        txt.style.color = '#999';
                        txt.style.fontSize = '13px';
                        e.target.parentElement.appendChild(txt);
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Sin imagen
                      </Typography>
                    </Box>
                  )}
                </Card>
                {formData.Imagen && (
                  <Typography variant="caption" color="text.secondary">
                    {formData.Imagen}
                  </Typography>
                )}
                <Divider sx={{ mt: 2, mb: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Cambiar imagen
                </Typography>
              </Grid>
            )}

            {/* Sección de carga de imagen */}
            {!editingServicio && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Imagen del Servicio *
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Nombre de Imagen"
                  value={formData.Imagen}
                  onChange={(e) => handleChange('Imagen', e.target.value)}
                  error={!!formErrors.Imagen}
                  helperText={formErrors.Imagen || 'O ingrese manualmente el nombre'}
                  size="small"
                  InputProps={{
                    startAdornment: <ImageIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ minWidth: '140px' }}
                >
                  Seleccionar
                </Button>
                {selectedFile && (
                  <Button
                    variant="contained"
                    onClick={handleUploadImage}
                    disabled={uploading}
                    sx={{ minWidth: '120px' }}
                  >
                    {uploading ? 'Subiendo...' : 'Subir'}
                  </Button>
                )}
              </Box>
              {selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Archivo seleccionado: {selectedFile.name}
                </Typography>
              )}
              {uploading && <LinearProgress sx={{ mt: 1 }} />}
            </Grid>

            {/* Preview de nueva imagen seleccionada */}
            {imagePreview?.startsWith('data:') && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Nueva imagen (previsualización)
                </Typography>
                <Card variant="outlined">
                  <CardMedia
                    component="img"
                    height="180"
                    image={imagePreview}
                    alt="Nueva imagen"
                    sx={{ objectFit: 'contain', bgcolor: 'grey.50' }}
                  />
                </Card>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={crearMutation.isLoading || actualizarMutation.isLoading}
          >
            {crearMutation.isLoading || actualizarMutation.isLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmación de Eliminación */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, servicioId: null })}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Está seguro que desea eliminar este servicio?</Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, servicioId: null })}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={eliminarMutation.isLoading}
          >
            {eliminarMutation.isLoading ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
